/** Structured HTTP error payload thrown by HttpInterceptorService. */
export interface HttpErrorPayload {
	status: number;
	errors?: string[];
	message?: string;
	code?: string;
	formNames?: string[];
	raw?: unknown;
}

export const HTTP_ERROR_MESSAGES = {
	network: "Network error. Check your connection and try again.",
	badRequest: "Invalid request. Please check your input.",
	unauthorized: "Your session has expired. Please sign in again.",
	forbidden: "You do not have permission to perform this action.",
	notFound: "The requested resource was not found.",
	conflict: "This action conflicts with the current state.",
	tooManyRequests: "Too many requests. Please wait and try again.",
	signOffPinLockout:
		"Sign Off PIN temporarily locked due to too many incorrect attempts. Please try again after 5 minutes.",
	payloadTooLarge: "This file is too large. Photos must be under 5 MB.",
	server: "Something went wrong on our end. Please try again later.",
	generic: "Something went wrong. Please try again.",
} as const;

export const SIGN_OFF_PIN_LOCKOUT_MODAL_TITLE = "Sign Off PIN Locked";

export const MAINTENANCE_MODAL_MESSAGE = "The application is currently under maintenance. Please try again later.";

const AUTH_TOKEN_MESSAGES = [
	"missing or invalid google id token",
	"please provide token",
	"token has expired",
	"invalid or missing token",
];

/** True for Angular default HTTP failure strings and other non-user-facing text. */
export function isTechnicalHttpMessage(message: unknown): boolean {
	if (typeof message !== "string") return true;
	const trimmed = message.trim();
	if (!trimmed) return true;
	const lower = trimmed.toLowerCase();
	if (lower.startsWith("http failure response")) return true;
	if (lower.startsWith("{") || lower.startsWith("[")) return true;
	if (lower === "undefined" || lower === "null" || lower === "[object object]") return true;
	if (
		lower.includes("request file too large") ||
		lower.includes("payload too large") ||
		lower.includes("request entity too large") ||
		lower.includes("request too big")
	) {
		return true;
	}
	return false;
}

export function isAuthTokenErrorMessage(message: unknown): boolean {
	if (typeof message !== "string") return false;
	const lower = message.toLowerCase().trim();
	return AUTH_TOKEN_MESSAGES.some((m) => lower.includes(m));
}

function normalizeErrors(value: unknown): string[] {
	if (Array.isArray(value)) {
		return value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
	}
	if (typeof value === "string" && value.trim()) {
		return [value.trim()];
	}
	return [];
}

function messageByStatus(status: number): string {
	if (!status || status === 0) return HTTP_ERROR_MESSAGES.network;
	if (status === 400 || status === 422) return HTTP_ERROR_MESSAGES.badRequest;
	if (status === 401) return HTTP_ERROR_MESSAGES.unauthorized;
	if (status === 403) return HTTP_ERROR_MESSAGES.forbidden;
	if (status === 404) return HTTP_ERROR_MESSAGES.notFound;
	if (status === 409) return HTTP_ERROR_MESSAGES.conflict;
	if (status === 413) return HTTP_ERROR_MESSAGES.payloadTooLarge;
	if (status === 429) return HTTP_ERROR_MESSAGES.tooManyRequests;
	if (status === 502 || status === 504) return HTTP_ERROR_MESSAGES.network;
	if (status >= 500) return HTTP_ERROR_MESSAGES.server;
	return HTTP_ERROR_MESSAGES.generic;
}

/**
 * Normalize interceptor / HttpErrorResponse / legacy shapes into a structured payload.
 */
export function toHttpErrorPayload(error: unknown): HttpErrorPayload {
	if (error == null) {
		return { status: 0 };
	}

	if (typeof error === "string") {
		return {
			status: 0,
			message: error,
			errors: isTechnicalHttpMessage(error) ? undefined : [error],
		};
	}

	if (Array.isArray(error)) {
		const errors = normalizeErrors(error);
		return { status: 0, errors, message: errors[0] };
	}

	const err = error as Record<string, unknown>;
	const nested = err["error"];
	const nestedObj =
		nested && typeof nested === "object" && !Array.isArray(nested) ? (nested as Record<string, unknown>) : null;

	const status =
		(typeof err["status"] === "number" ? err["status"] : undefined) ??
		(typeof err["statusCode"] === "number" ? err["statusCode"] : undefined) ??
		(typeof nestedObj?.["status"] === "number" ? nestedObj["status"] : undefined) ??
		(typeof nestedObj?.["statusCode"] === "number" ? nestedObj["statusCode"] : undefined) ??
		0;

	const errors = normalizeErrors(
		err["errors"] ??
			nestedObj?.["errors"] ??
			(typeof nested === "string" ? nested : undefined) ??
			(Array.isArray(nested) ? nested : undefined),
	);

	const messageCandidates = [
		typeof err["message"] === "string" ? err["message"] : undefined,
		typeof err["description"] === "string" ? err["description"] : undefined,
		typeof nestedObj?.["message"] === "string" ? nestedObj["message"] : undefined,
		typeof nested === "string" ? nested : undefined,
		errors[0],
	].filter((m): m is string => typeof m === "string" && m.trim().length > 0);

	return {
		status,
		errors: errors.length ? errors : undefined,
		message: messageCandidates[0],
		code: readOptionalString(err["code"]) ?? readOptionalString(nestedObj?.["code"]),
		formNames: readStringList(err["formNames"]) ?? readStringList(nestedObj?.["formNames"]),
		raw: error,
	};
}

function readOptionalString(value: unknown): string | undefined {
	return typeof value === "string" && value.trim() ? value : undefined;
}

function readStringList(value: unknown): string[] | undefined {
	if (!Array.isArray(value)) return undefined;
	const items = value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
	return items.length ? items : undefined;
}

/**
 * Resolve a user-facing error message for modals/snackbars.
 * Prefers non-technical backend text; otherwise maps by HTTP status.
 */
export function resolveHttpErrorMessage(error: unknown): string | string[] {
	const payload = toHttpErrorPayload(error);
	const usableErrors = (payload.errors ?? []).filter((e) => !isTechnicalHttpMessage(e));

	if (usableErrors.length > 1) {
		return usableErrors;
	}
	if (usableErrors.length === 1) {
		return usableErrors[0];
	}

	if (payload.message && !isTechnicalHttpMessage(payload.message)) {
		return payload.message;
	}

	return messageByStatus(payload.status);
}

/** Single-string form for snackbars and places that cannot show arrays. */
export function resolveHttpErrorMessageText(error: unknown): string {
	const resolved = resolveHttpErrorMessage(error);
	if (Array.isArray(resolved)) {
		return resolved[0] || HTTP_ERROR_MESSAGES.generic;
	}
	return resolved || HTTP_ERROR_MESSAGES.generic;
}

/**
 * Content for the Under Maintenance dialog. Null unless the response is HTTP 503.
 * Uses server `errors` when present (HttpErrorResponse or interceptor payload).
 */
export function resolveMaintenanceModalContent(error: unknown): string[] | null {
	const payload = toHttpErrorPayload(error);
	if (payload.status !== 503) return null;
	const errors = (payload.errors ?? []).filter((item) => item.trim().length > 0);
	return errors.length > 0 ? errors : [MAINTENANCE_MODAL_MESSAGE];
}

/** Sign-off flows: dedicated lockout title/message for HTTP 429. */
export function resolveSignOffHttpErrorPresentation(error: unknown): {
	title: string;
	message: string | string[];
} {
	const payload = toHttpErrorPayload(error);

	if (payload.status === 429) {
		const message =
			payload.message && !isTechnicalHttpMessage(payload.message)
				? payload.message
				: HTTP_ERROR_MESSAGES.signOffPinLockout;
		return {
			title: SIGN_OFF_PIN_LOCKOUT_MODAL_TITLE,
			message,
		};
	}

	return {
		title: "Error",
		message: resolveHttpErrorMessage(error),
	};
}
